import React, { useState, useEffect, useMemo } from 'react';
import { incluyeTexto } from '../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Utensils, Coffee, Pizza, Plus, Clock, CheckCircle2, XCircle,
    Search, PlayCircle, Loader2, Info, ChevronDown, Check, Download, FileSpreadsheet, AlertTriangle,
    Cookie, Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import TableView from './TableView';
import MenuComidaAdmin from './MenuComidaAdmin';
import ComidaDashboard from './comida/ComidaDashboard';
import CierrePedidoDia from './comida/CierrePedidoDia';
import ArmarRefrigerio from './comida/ArmarRefrigerio';
import { buildMenu, etiquetaBebida, formatoMonto, hora12 } from '../utils/menuComida';
import { useSearchParams } from 'react-router-dom';

// ─── Constantes y Menús ────────────────────────────────────────────────────────
const HORARIOS_CORTES = {
    'Desayuno': 8, // 8 AM
    'Almuerzo': 12, // 12 PM
    'Cena': 19, // 7 PM
};

const ESTADO_CONFIG = {
    pendiente: { label: 'Pendiente líder', icon: Clock, cls: 'bg-gray-50 text-sura-blue border-gray-50' },
    aprobada: { label: 'En proceso', icon: Utensils, cls: 'bg-gray-50 text-sura-blue border-gray-50' },
    en_gestion: { label: 'En preparación', icon: PlayCircle, cls: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
    completada: { label: 'Entregada', icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
    rechazada: { label: 'Rechazada', icon: XCircle, cls: 'bg-red-50 text-red-600 border-red-100' },
    cancelada: { label: 'Cancelada', icon: XCircle, cls: 'bg-gray-100 text-gray-500 border-gray-200' },
};

// Las que todavia se pueden quitar del pedido (no entregadas, rechazadas ni canceladas).
const ESTADOS_CANCELABLES = ['pendiente', 'aprobada', 'en_gestion'];
const esCancelable = (sol) => !sol.isGroup && ESTADOS_CANCELABLES.includes(sol.estado);

// Refrigerio para eventos: el pedido (combos) lo arma Procura desde su bandeja.
const REFRIGERIO = 'Refrigerio';
const esRefrigerio = (sol) => !sol?.isGroup && sol?.tipo === REFRIGERIO;
const porArmar = (sol) => esRefrigerio(sol) && !sol.refrigerio?.armado;
const REFRIGERIO_VACIO = { hora: '', motivo: '', personas: '', items: [] };

// "MINI TEQUEÑOS" -> "Mini tequeños" (el menu guarda los nombres en mayusculas)
const nombreItem = (t) => {
    const s = String(t || '').trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
};

// Celda "Comida" de un refrigerio: etiquetas por item, personas/hora y el motivo
const ComidaRefrigerio = ({ sol }) => {
    const r = sol.refrigerio || {};
    const items = r.items?.length ? r.items : String(sol.comida_principal || '').split(',').filter(x => x.trim());
    return (
        <div className="min-w-[220px] max-w-[300px] space-y-1.5">
            <div className="flex flex-wrap gap-1">
                {items.map((it, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium whitespace-nowrap">
                        {nombreItem(it)}
                    </span>
                ))}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
                {r.personas ? <span className="inline-flex items-center gap-1 whitespace-nowrap"><Users size={12} /> {r.personas} {Number(r.personas) === 1 ? 'persona' : 'personas'}</span> : null}
                {r.hora ? <span className="inline-flex items-center gap-1 whitespace-nowrap"><Clock size={12} /> {hora12(r.hora)}</span> : null}
            </div>
            {r.motivo && <p className="text-xs text-gray-400 truncate" title={r.motivo}>{r.motivo}</p>}
        </div>
    );
};

const BadgePorArmar = () => (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
        <AlertTriangle size={11} /> Por armar
    </span>
);

// Grupos de comida externa: cada uno se carga a su departamento y centro de costo
// fijos (los define el backend en models/solicitud_comida.py EXTERNOS).
const EXTERNOS = [
    { id: 'solifast', label: 'Solifast', departamento: 'ALMACEN (SOLIFAST)', centroCosto: '011' },
    { id: 'seguridad', label: 'Seguridad', departamento: 'SEGURIDAD (EXTERNO)', centroCosto: '013' },
];
// En el selector de empleado un externo va como 'externo:<id>'
const externoDe = (valor) => EXTERNOS.find(x => `externo:${x.id}` === valor);

// Monto de una solicitud (o de un grupo, sumando sus ítems) para la bandeja de Procura.
const montoDe = (sol) => {
    if (sol.isGroup) {
        // Rechazadas y canceladas no suman (igual que en reportes y dashboard)
        const conGasto = sol.items.filter(i => i.estado !== 'rechazada' && i.estado !== 'cancelada');
        const conMonto = conGasto.filter(i => i.monto !== null && i.monto !== undefined);
        return {
            monto: conMonto.length ? conMonto.reduce((t, i) => t + Number(i.monto), 0) : null,
            incompleto: conMonto.length < conGasto.length || conGasto.some(i => i.monto_incompleto),
        };
    }
    return {
        monto: sol.monto === null || sol.monto === undefined ? null : Number(sol.monto),
        incompleto: Boolean(sol.monto_incompleto),
    };
};

const MontoCelda = ({ sol, className = 'text-sm' }) => {
    if (porArmar(sol)) return <BadgePorArmar />;
    const { monto, incompleto } = montoDe(sol);
    if (monto === null) return <span className="text-xs text-gray-300 italic">Sin precio</span>;
    return (
        <span className={`inline-flex items-center gap-1 font-semibold tabular-nums text-gray-900 ${className}`}
            title={incompleto ? 'Algún ítem no tenía precio al momento de pedir: el monto real puede ser mayor' : undefined}>
            {formatoMonto(monto)}
            {incompleto && <AlertTriangle size={12} className="text-amber-500" />}
        </span>
    );
};

const etiquetaCentroCosto = (sol) =>
    sol.centro_costo ? `${sol.centro_costo}${sol.centro_costo_nombre ? ` · ${sol.centro_costo_nombre}` : ''}` : null;

const EstadoBadge = ({ estado }) => {
    const c = ESTADO_CONFIG[estado] || ESTADO_CONFIG.pendiente;
    const Icon = c.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider border whitespace-nowrap ${c.cls}`}>
            <Icon size={12} /> {c.label}
        </span>
    );
};

// ─── Componente Principal ──────────────────────────────────────────────────
const SolicitudComidaView = () => {
    const { user } = useAuth();
    // El backend decide quien gestiona (rol procura/procura_nacional, area de
    // Procura u observador especial) y lo manda en el usuario. El fallback cubre
    // sesiones viejas cuyo token se guardo antes de que existiera el campo.
    const esProcura = user?.ve_gestion_procura
        ?? ['procura', 'procura_nacional'].includes(user?.role);
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');

    const [viewMode, setViewMode] = useState(tabParam || (esProcura ? 'bandeja' : 'create'));
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    const [misSolicitudes, setMisSolicitudes] = useState([]);
    const [fechaMisSolicitudes, setFechaMisSolicitudes] = useState(new Date().toISOString().split('T')[0]);
    
    const processedMisSolicitudes = useMemo(() => {
        return misSolicitudes.filter(item => item.fecha_consumo === fechaMisSolicitudes);
    }, [misSolicitudes, fechaMisSolicitudes]);

    const [bandeja, setBandeja] = useState([]);
    const [pendientesAprobar, setPendientesAprobar] = useState([]);
    const [selectedSol, setSelectedSol] = useState(null);
    const [filterEstado, setFilterEstado] = useState('all');
    const [columnVisibility, setColumnVisibility] = useState({});
    const [isSupervisor, setIsSupervisor] = useState(false);

    // Estado para Reportes
    const [reportData, setReportData] = useState(null);
    const [reportParams, setReportParams] = useState({
        fecha: new Date().toISOString().split('T')[0],
        tipo: 'Todos'
    });

    // Pedidos Grupales
    const [isGroupOrder, setIsGroupOrder] = useState(false);
    const [groupCart, setGroupCart] = useState([]);
    const [groupOrderSize, setGroupOrderSize] = useState('1'); // "999" para sin límite
    const [groupOrderCountSet, setGroupOrderCountSet] = useState(false);

    // Estado Desayuno
    const [desayunoSelections, setDesayunoSelections] = useState([]); // {id, tipo, relleno}
    const [desayunoBebida, setDesayunoBebida] = useState('');
    const [showConfirmGroupModal, setShowConfirmGroupModal] = useState(false);
    const [selectedGroupToConfirm, setSelectedGroupToConfirm] = useState(null);
    const [empleadosList, setEmpleadosList] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const menu = useMemo(() => buildMenu(menuItems), [menuItems]);
    const [selectedEmpleadoId, setSelectedEmpleadoId] = useState('');
    const [empleadoSearch, setEmpleadoSearch] = useState('');
    const [isEmpleadoSelectOpen, setIsEmpleadoSelectOpen] = useState(false);
    const [showCancelGroupModal, setShowCancelGroupModal] = useState(false);
    const [solACancelar, setSolACancelar] = useState(null); // solicitud a quitar (modal de confirmacion)
    const [refri, setRefri] = useState(REFRIGERIO_VACIO); // formulario de refrigerio
    const [solArmar, setSolArmar] = useState(null); // refrigerio cuyo pedido arma Procura

    // Formulario
    const [tipoComida, setTipoComida] = useState(''); // Desayuno, Almuerzo, Cena
    const [formData, setFormData] = useState({
        comida_principal: '',
        tipo_categoria: '', // e.g. CARNES, PIZZAS
        contornos: [], // array for almuerzo
        bebida: '',
        observaciones: '',
        fecha_consumo: new Date().toISOString().split('T')[0]
    });
    // ============================================
    // AGRUPACIÓN DE BANDEJA PROCURA
    // ============================================
    const [fechaBandeja, setFechaBandeja] = useState(new Date().toISOString().split('T')[0]);

    const processedBandeja = useMemo(() => {
        if (viewMode !== 'bandeja') return [];
        const groups = {};
        const flatList = [];

        bandeja.forEach(item => {
            if (item.fecha_consumo !== fechaBandeja) return;

            if (item.pedido_grupal_id) {
                const proxyName = item.gestionado_por_nombre || 'Un Usuario';
                const groupKey = item.pedido_grupal_id;
                
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        isGroup: true,
                        id: `group_${groupKey}`, 
                        solicitante_nombre: `Pedido Grupal`,
                        departamento: 'Varios',
                        fecha_consumo: item.fecha_consumo,
                        created_at: item.created_at,
                        tipo: item.tipo,
                        estado: 'en_gestion', 
                        comida_principal: 'Ver detalles del grupo...',
                        observaciones: `Agrupación Automática`,
                        items: []
                    };
                }
                groups[groupKey].items.push(item);
            } else {
                flatList.push(item);
            }
        });

        Object.values(groups).forEach(g => {
            const activos = g.items.filter(i => i.estado !== 'rechazada' && i.estado !== 'cancelada');
            g.solicitante_nombre = `${g.solicitante_nombre} (${activos.length} personas)`;
            if (activos.length === 0) g.estado = g.items.some(i => i.estado === 'rechazada') ? 'rechazada' : 'cancelada';
            else if (activos.every(i => i.estado === 'completada')) g.estado = 'completada';
            else g.estado = 'en_gestion';
            
            flatList.push(g);
        });

        return flatList.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }, [bandeja, viewMode, fechaBandeja]);

    const openConfirmGroupModal = (grupo) => {
        setSelectedGroupToConfirm(grupo);
        setShowConfirmGroupModal(true);
    };

    const confirmMarcarGrupoEntregado = async () => {
        if (!selectedGroupToConfirm) return;
        setSubmitting(true);
        try {
            await Promise.all(
                selectedGroupToConfirm.items
                    .filter(i => ESTADOS_CANCELABLES.includes(i.estado))
                    .map(item => axiosInstance.put(`/api/comidas/${item.id}/estado`, { estado: 'completada' }))
            );
            toast.success(`Se han entregado los pedidos del grupo exitosamente.`);
            setShowConfirmGroupModal(false);
            setSelectedGroupToConfirm(null);
            setSelectedSol(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al marcar grupo');
        } finally {
            setSubmitting(false);
        }
    };

    const handleMarcarGrupoEntregado = async (grupo) => {
        openConfirmGroupModal(grupo);
    };
    
    useEffect(() => {
        if (tabParam) setViewMode(tabParam);
    }, [tabParam]);

    // Bloquear scroll cuando el modal está abierto
    useEffect(() => {
        if (selectedSol) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [selectedSol]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [viewMode, filterEstado, reportParams]);

    const cargarMenu = async () => {
        try {
            const res = await axiosInstance.get('/api/comidas/menu');
            setMenuItems(res.data);
        } catch (err) {
            console.error('Error cargando el menú', err);
            toast.error('No se pudo cargar el menú de comida');
        }
    };

    useEffect(() => {
        cargarMenu();
    }, []);

    useEffect(() => {
        if (isGroupOrder && empleadosList.length === 0) {
            axiosInstance.get('/api/empleados')
                .then(res => setEmpleadosList(res.data))
                .catch(err => console.error('Error fetching empleados', err));
        }
    }, [isGroupOrder, empleadosList.length]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (viewMode === 'list') {
                const res = await axiosInstance.get('/api/comidas/mis-solicitudes');
                setMisSolicitudes(res.data.solicitudes || []);
                if (res.data.es_supervisor !== undefined) setIsSupervisor(res.data.es_supervisor);
            } else if (viewMode === 'aprobaciones') {
                const res = await axiosInstance.get('/api/comidas/pendientes-aprobar');
                setPendientesAprobar(res.data.solicitudes || []);
                if (res.data.es_supervisor !== undefined) setIsSupervisor(res.data.es_supervisor);
            } else if (viewMode === 'bandeja' && esProcura) {
                const res = await axiosInstance.get(`/api/comidas/?estado=${filterEstado}`);
                setBandeja(res.data);
            } else if (viewMode === 'reportes' && esProcura) {
                const res = await axiosInstance.get(`/api/comidas/reporte?fecha=${reportParams.fecha}&tipo=${reportParams.tipo}`);
                setReportData(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ¿Se está pidiendo el turno con menos de 4 horas de anticipación? Es solo un
    // aviso: igual se puede pedir (Procura decide si alcanza a prepararlo).
    const esFueraDeTiempo = (turno) => {
        if (turno === 'Cena') return false;
        if (turno === REFRIGERIO) {
            // Refrigerio: 4 horas antes de la hora que indicó la persona
            if (!refri.hora || !formData.fecha_consumo) return false;
            const evento = new Date(`${formData.fecha_consumo}T${refri.hora}:00`);
            return !Number.isNaN(evento.getTime()) && evento.getTime() - Date.now() < 4 * 60 * 60 * 1000;
        }

        const todayStr = new Date().toISOString().split('T')[0];
        if (formData.fecha_consumo !== todayStr) return false; // Otro día: siempre a tiempo

        const now = new Date();
        const horaCorte = HORARIOS_CORTES[turno] - 4; // 4 horas antes

        return !(now.getHours() < horaCorte || (now.getHours() === horaCorte && now.getMinutes() === 0));
    };


    const handleContornoToggle = (contorno) => {
        setFormData(prev => {
            const isSelected = prev.contornos.includes(contorno);
            if (isSelected) {
                return { ...prev, contornos: prev.contornos.filter(c => c !== contorno) };
            } else {
                if (prev.contornos.length >= 2) {
                    toast.error('Máximo 2 contornos permitidos');
                    return prev;
                }
                return { ...prev, contornos: [...prev.contornos, contorno] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!tipoComida) return toast.error('Seleccione un turno (Desayuno, Almuerzo, Cena o Refrigerio)');

        if (tipoComida === REFRIGERIO) {
            if (!refri.hora) return toast.error('Indica la hora en que se necesita el refrigerio');
            if (!refri.motivo.trim()) return toast.error('Indica el motivo del evento');
            const personas = Number(refri.personas);
            if (!Number.isInteger(personas) || personas < 1) return toast.error('Indica cuántas personas asistirán');
            if (refri.items.length === 0) return toast.error('Elige al menos un refrigerio');
            setSubmitting(true);
            try {
                await axiosInstance.post('/api/comidas/', {
                    tipo: REFRIGERIO,
                    fecha_consumo: formData.fecha_consumo,
                    hora: refri.hora,
                    motivo: refri.motivo.trim(),
                    personas,
                    items: refri.items,
                    observaciones: formData.observaciones,
                });
                toast.success('Refrigerio enviado a Procura');
                setViewMode('list');
                setTipoComida('');
                setRefri(REFRIGERIO_VACIO);
                setFormData({
                    comida_principal: '', tipo_categoria: '', contornos: [], bebida: '',
                    observaciones: '', fecha_consumo: new Date().toISOString().split('T')[0]
                });
                fetchData();
            } catch (error) {
                toast.error(error.response?.data?.error || 'Error al enviar el refrigerio');
            } finally {
                setSubmitting(false);
            }
            return;
        }

        const payload = {
            tipo: tipoComida,
            fecha_consumo: formData.fecha_consumo,
            observaciones: formData.observaciones,
            bebida: formData.bebida,
        };

        if (tipoComida === 'Desayuno') {
            if (desayunoSelections.length === 0) return toast.error('Debe agregar al menos 1 pieza para el desayuno');
            if (!desayunoBebida) return toast.error('Debe seleccionar una bebida para el desayuno');

            // Todas las piezas son del mismo tipo, e.g., "Empanada x2: Carne Mechada, Queso".
            // Sin plural: el nombre lo pone Procura y puede ser cualquier cosa ("Tequeños").
            const pieza = desayunoSelections[0].tipo;
            const rellenos = desayunoSelections.map(s => s.relleno).join(', ');
            payload.comida_principal = `${pieza} x${desayunoSelections.length}: ${rellenos}`;
            payload.tipo_categoria = 'DESAYUNO';
            payload.bebida = desayunoBebida;
        } else {
            if (!formData.comida_principal) return toast.error('Debe seleccionar una comida principal');
            
            payload.comida_principal = formData.comida_principal;
            if (tipoComida === 'Almuerzo') {
                payload.contorno_1 = formData.contornos[0] || '';
                payload.contorno_2 = formData.contornos[1] || '';
            }
        }

        if (isGroupOrder) {
            if (groupCart.length >= Number(groupOrderSize)) {
                return toast.error(`Ya has alcanzado el límite de ${groupOrderSize} personas para este pedido grupal.`);
            }

            let empName = '';
            const externo = externoDe(selectedEmpleadoId);
            if (externo) {
                const numExternos = groupCart.filter(item => item.externo === externo.id).length + 1;
                empName = `Comida externa ${externo.label} ${numExternos}`;
                payload.empleado_id = 'externo';
                payload.empleado_nombre = empName;
                payload.is_externo = true;
                payload.externo = externo.id;
            } else {
                if (!selectedEmpleadoId) return toast.error('Seleccione un empleado');
                const emp = empleadosList.find(e => e.id === Number(selectedEmpleadoId));
                empName = emp ? (emp.nombre_completo || emp.nombre) : 'Desconocido';
                payload.empleado_id = Number(selectedEmpleadoId);
                payload.empleado_nombre = empName;
            }
            
            setGroupCart(prev => [...prev, payload]);
            toast.success(`Agregado a la lista: ${empName}`);
            
            // Limpiar formulario parcial
            setSelectedEmpleadoId('');
            setTipoComida('');
            setFormData(prev => ({
                ...prev,
                comida_principal: '',
                tipo_categoria: '',
                contornos: [],
                bebida: '',
                observaciones: ''
            }));
            return;
        }

        setSubmitting(true);
        try {
            await axiosInstance.post('/api/comidas/', payload);
            toast.success('Solicitud enviada con éxito');
            setViewMode('list');
            setTipoComida('');
            setFormData({
                comida_principal: '', tipo_categoria: '', contornos: [], bebida: '',
                observaciones: '', fecha_consumo: new Date().toISOString().split('T')[0]
            });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al enviar solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    const submitGroupCart = async () => {
        if (groupCart.length === 0) return toast.error('La lista está vacía');
        setSubmitting(true);
        try {
            const pedidos = groupCart;
            await axiosInstance.post('/api/comidas/batch', { pedidos });
            toast.success(`Pedido grupal de ${groupCart.length} solicitudes enviado con éxito`);
            setGroupCart([]);
            setIsGroupOrder(false);
            setViewMode('list');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al enviar pedido grupal');
        } finally {
            setSubmitting(false);
        }
    };

    const cambiarEstado = async (id, nuevoEstado, motivo = '') => {
        try {
            await axiosInstance.put(`/api/comidas/${id}/estado`, { estado: nuevoEstado, motivo_rechazo: motivo });
            toast.success('Estado actualizado exitosamente');
            fetchData();
            setSelectedSol(null);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al cambiar estado');
        }
    };

    const cancelarSolicitud = (sol) => setSolACancelar(sol);

    const confirmarCancelacion = async () => {
        const sol = solACancelar;
        if (!sol) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.put(`/api/comidas/${sol.id}/cancelar`);
            toast.success('Solicitud cancelada');
            // Si se quito desde el detalle de un grupo, el modal sigue abierto con el item al dia
            setSelectedSol(prev => prev?.isGroup
                ? { ...prev, items: prev.items.map(i => i.id === sol.id ? { ...i, ...res.data } : i) }
                : null);
            setSolACancelar(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al cancelar la solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    // ============================================
    // RENDER: Formulario de Creación
    // ============================================
    const renderCreate = () => (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4">
                <p className="text-gray-600 text-sm leading-relaxed flex items-start gap-3">
                    <Info size={18} className="shrink-0 mt-0.5 text-gray-400" />
                    <span>Recuerde que debe realizar la solicitud con un mínimo de <strong>4 horas de anticipación</strong> a la hora del turno.</span>
                </p>
            </div>

            {/* Toggle Individual / Grupal */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
                <button 
                    type="button" 
                    onClick={() => { 
                        if (isGroupOrder && groupCart.length > 0) {
                            setShowCancelGroupModal(true);
                            return;
                        }
                        setIsGroupOrder(false); 
                        setGroupOrderCountSet(false); 
                        setGroupCart([]); 
                    }} 
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!isGroupOrder ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Mi Pedido
                </button>
                <button type="button" onClick={() => { setIsGroupOrder(true); if (tipoComida === REFRIGERIO) setTipoComida(''); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isGroupOrder ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Pedido Grupal</button>
            </div>

            {isGroupOrder && !groupOrderCountSet ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm max-w-md mx-auto text-center space-y-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800">¿Para cuántas personas pedirás comida?</h3>
                        <p className="text-sm text-sura-blue mt-2">Ingresa la cantidad total de personas (incluyendo externos) que conforman este pedido grupal.</p>
                    </div>
                    
                    <div className="flex flex-col items-center gap-4">
                        <input 
                            type="number" 
                            min="1" 
                            max="200"
                            placeholder="Ej: 15"
                            value={groupOrderSize === '999' ? '' : groupOrderSize}
                            onChange={e => setGroupOrderSize(e.target.value)}
                            className="w-32 mx-auto text-center px-4 py-3 text-2xl bg-gray-50 text-gray-800 font-bold rounded-xl border-2 border-gray-300 focus:border-sura-blue focus:ring-4 focus:ring-gray-50 transition-all outline-none placeholder:text-gray-300 placeholder:font-normal"
                        />
                        <button 
                            type="button"
                            onClick={() => {
                                if (!groupOrderSize || Number(groupOrderSize) <= 0 || Number(groupOrderSize) === 999) return toast.error('Ingresa una cantidad válida');
                                setGroupOrderCountSet(true);
                            }}
                            className="w-full py-3.5 bg-sura-blue hover:bg-sura-blue-dark text-white rounded-xl font-bold shadow-lg shadow-sura-blue/20 transition-all"
                        >
                            Comenzar a armar el pedido
                        </button>
                    </div>

                    <div className="pt-2 border-t border-gray-100 w-full mt-2">
                        <button 
                            type="button"
                            onClick={() => {
                                setGroupOrderSize('999');
                                setGroupOrderCountSet(true);
                            }}
                            className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-all border border-gray-200"
                        >
                            Agregar sin límite
                        </button>
                    </div>
                </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
                {isGroupOrder && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Empleado (Para quién es la comida)</label>
                        
                        <div className="relative">
                            <div 
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white cursor-pointer flex justify-between items-center hover:border-gray-100 transition-colors"
                                onClick={() => setIsEmpleadoSelectOpen(true)}
                            >
                                <span className={selectedEmpleadoId ? "text-gray-800 font-medium" : "text-gray-400"}>
                                    {externoDe(selectedEmpleadoId) ? `Comida externa ${externoDe(selectedEmpleadoId).label} (${externoDe(selectedEmpleadoId).departamento})` : (selectedEmpleadoId ? (empleadosList.find(e => e.id === Number(selectedEmpleadoId))?.nombre_completo || 'Empleado Seleccionado') : '-- Buscar y Seleccionar Empleado --')}
                                </span>
                                <Search size={20} className="text-gray-400" />
                            </div>

                            <AnimatePresence>
                                {isEmpleadoSelectOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsEmpleadoSelectOpen(false)}></div>
                                        <motion.div 
                                            initial={{ opacity: 0, y: -10 }} 
                                            animate={{ opacity: 1, y: 0 }} 
                                            exit={{ opacity: 0, y: -10 }}
                                            className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
                                        >
                                            <div className="p-3 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                                                <Search size={18} className="text-gray-400 shrink-0" />
                                                <input 
                                                    autoFocus
                                                    type="text" 
                                                    placeholder="Escriba nombre, apellido o cédula..." 
                                                    value={empleadoSearch} 
                                                    onChange={e => setEmpleadoSearch(e.target.value)}
                                                    className="w-full bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
                                                />
                                                <button type="button" onClick={() => setIsEmpleadoSelectOpen(false)} className="text-gray-400 hover:text-gray-600 shrink-0"><XCircle size={18} /></button>
                                            </div>
                                            <div className="max-h-64 overflow-y-auto">
                                                {EXTERNOS.map(ext => (
                                                    <div 
                                                        key={ext.id}
                                                        onClick={() => { setSelectedEmpleadoId(`externo:${ext.id}`); setIsEmpleadoSelectOpen(false); setEmpleadoSearch(''); }}
                                                        className="px-4 py-3 bg-gray-50 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors flex items-center gap-2"
                                                    >
                                                        <Plus size={18} className="text-sura-blue" />
                                                        <span className="font-bold text-sura-blue">Añadir Comida externa {ext.label}</span>
                                                        <span className="ml-auto text-xs text-gray-500">Se carga a {ext.departamento} · CC {ext.centroCosto}</span>
                                                    </div>
                                                ))}
                                                {(() => {
                                                    const filtered = empleadosList.filter(e => {
                                                        const alreadyAdded = groupCart.some(item => item.empleado_id === e.id);
                                                        if (alreadyAdded) return false;
                                                        
                                                        return incluyeTexto(e.nombre_completo, empleadoSearch) ||
                                                               (e.cedula || '').includes(empleadoSearch);
                                                    }).slice(0, 50);

                                                    if (filtered.length === 0) {
                                                        return <div className="p-6 text-center text-sm text-sura-blue">No se encontraron empleados que coincidan</div>;
                                                    }

                                                    return filtered.map(e => (
                                                        <div 
                                                            key={e.id} 
                                                            onClick={() => { setSelectedEmpleadoId(e.id); setIsEmpleadoSelectOpen(false); setEmpleadoSearch(''); }}
                                                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors group"
                                                        >
                                                            <p className="font-semibold text-gray-800 group-hover:text-sura-blue">{e.nombre_completo}</p>
                                                            <p className="text-xs text-sura-blue mt-0.5">
                                                                <span className="font-medium text-gray-600">C.I:</span> {e.cedula} &nbsp;•&nbsp; 
                                                                {e.departamento || e.cargo || 'Sin Dpto'}
                                                            </p>
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                )}

                {/* Fecha */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Fecha de consumo</label>
                    <input type="date" value={formData.fecha_consumo} min={new Date().toISOString().split('T')[0]}
                        onChange={e => setFormData({ ...formData, fecha_consumo: e.target.value })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue" required />
                </div>

                {/* Turno */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">1. Seleccione el Turno</h3>
                    <div className={`grid grid-cols-1 gap-4 ${isGroupOrder ? 'md:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
                        {/* El refrigerio ya es para un grupo: no va en el pedido grupal */}
                        {['Desayuno', 'Almuerzo', 'Cena', ...(isGroupOrder ? [] : [REFRIGERIO])].map(turno => {
                            const tarde = esFueraDeTiempo(turno);
                            const Icon = turno === 'Desayuno' ? Coffee : turno === 'Almuerzo' ? Utensils : turno === REFRIGERIO ? Cookie : Pizza;
                            return (
                                <button key={turno} type="button"
                                    onClick={() => {
                                        setTipoComida(turno);
                                        setFormData(p => ({ ...p, comida_principal: '', tipo_categoria: '', contornos: [], bebida: '' }));
                                        setDesayunoSelections([]);
                                        setDesayunoBebida('');
                                        setRefri(REFRIGERIO_VACIO);
                                    }}
                                    className={`relative p-5 rounded-2xl border-2 transition-all flex flex-col items-center gap-3
                                        ${tipoComida === turno ? 'border-sura-blue bg-gray-50' : 'border-gray-100 hover:border-gray-100 hover:bg-gray-50/30'}`}
                                >
                                    <Icon size={28} className={tipoComida === turno ? 'text-sura-blue' : 'text-gray-600'} />
                                    <span className={`font-semibold ${tipoComida === turno ? 'text-sura-blue' : 'text-gray-700'}`}>{turno}</span>
                                    {tarde && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full tracking-wider">
                                            <Clock size={10} /> Menos de 4 h de anticipación
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {tipoComida && esFueraDeTiempo(tipoComida) && (
                        <div className="mt-4 flex items-start gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-500" />
                            <span>
                                Faltan menos de 4 horas para el {tipoComida.toLowerCase()}. Igual puedes enviar la solicitud,
                                pero Procura podría no alcanzar a {tipoComida === REFRIGERIO ? 'tenerlo listo' : 'prepararla'} a tiempo.
                            </span>
                        </div>
                    )}
                </div>

                {/* Menú Desayuno */}
                {tipoComida === 'Desayuno' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">2. Arma tu Desayuno</h3>
                        <p className="text-xs text-sura-blue font-medium bg-gray-50 p-3 rounded-lg border border-gray-100">
                            Puedes pedir un máximo de <span className="font-bold">3 unidades en total</span>. Todas deben ser del mismo tipo de pieza.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Agregar pieza */}
                            <div className="space-y-4">
                                {menu.Desayuno.piezas.length === 0 ? (
                                    <p className="text-sm text-gray-500 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                        No hay piezas de desayuno disponibles en este momento. Si crees que es un error, avísale a Procura.
                                    </p>
                                ) : (
                                    <>
                                        <div>
                                            <label className="block text-xs text-sura-blue mb-1">Tipo de Pieza</label>
                                            <select id="desayunoTipo" className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue">
                                                {menu.Desayuno.piezas.map(p => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-sura-blue mb-1">Relleno</label>
                                            <select id="desayunoRelleno" className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue">
                                                {menu.Desayuno.rellenos.map(r => <option key={r.id} value={r.nombre}>{r.nombre}</option>)}
                                            </select>
                                        </div>
                                        <button type="button" onClick={() => {
                                            const tipo = document.getElementById('desayunoTipo').value;
                                            const relleno = document.getElementById('desayunoRelleno').value;
                                            if (!tipo) {
                                                toast.error('No hay piezas disponibles en el menú');
                                                return;
                                            }
                                            if (!relleno) {
                                                toast.error('No hay rellenos disponibles en el menú');
                                                return;
                                            }
                                    
                                            if (desayunoSelections.length >= 3) {
                                                toast.error('Ya alcanzaste el máximo de 3 piezas permitidas en total');
                                                return;
                                            }
                                            if (desayunoSelections.length > 0 && desayunoSelections[0].tipo !== tipo) {
                                                toast.error(`Ya elegiste ${desayunoSelections[0].tipo}: todas las piezas deben ser del mismo tipo.`);
                                                return;
                                            }
                                    
                                            setDesayunoSelections([...desayunoSelections, { id: Date.now(), tipo, relleno }]);
                                        }} className="w-full px-4 py-2 bg-sura-blue hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors flex justify-center items-center gap-2">
                                            <Plus size={16} /> Agregar Pieza
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Resumen piezas */}
                            <div className="border-l border-gray-100 pl-0 md:pl-6">
                                <h4 className="text-xs font-semibold text-gray-700 mb-3">Piezas agregadas ({desayunoSelections.length})</h4>
                                {desayunoSelections.length === 0 ? (
                                    <p className="text-sm text-gray-400 italic">No has agregado ninguna pieza aún.</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {desayunoSelections.map(item => (
                                            <li key={item.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                                                <span className="text-sm font-medium text-gray-700">{item.tipo} - <span className="text-gray-500 font-normal">{item.relleno}</span></span>
                                                <button type="button" onClick={() => setDesayunoSelections(desayunoSelections.filter(s => s.id !== item.id))} className="text-red-500 hover:text-red-700 p-1">
                                                    <XCircle size={16} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-100">
                            <label className="block text-xs text-sura-blue mb-1">Bebida</label>
                            <select value={desayunoBebida} onChange={e => setDesayunoBebida(e.target.value)} className="w-full md:w-1/2 px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue">
                                <option value="">Seleccione una bebida...</option>
                                {menu.Desayuno.bebidas.map(b => <option key={b.id} value={b.nombre}>{etiquetaBebida(b)}</option>)}
                            </select>
                        </div>
                    </motion.div>
                )}

                {/* Menú Almuerzo */}
                {tipoComida === 'Almuerzo' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">2. Comida Principal</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-sura-blue mb-1">Categoría</label>
                                    <select value={formData.tipo_categoria} onChange={e => setFormData({ ...formData, tipo_categoria: e.target.value, comida_principal: '' })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue">
                                        <option value="">Seleccione...</option>
                                        {Object.keys(menu.Almuerzo.platos).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-sura-blue mb-1">Plato</label>
                                    <select value={formData.comida_principal} onChange={e => setFormData({ ...formData, comida_principal: e.target.value })} disabled={!formData.tipo_categoria}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue disabled:opacity-50">
                                        <option value="">Seleccione un plato...</option>
                                        {formData.tipo_categoria && menu.Almuerzo.platos[formData.tipo_categoria]?.map(plato => <option key={plato.id} value={plato.nombre}>{plato.nombre}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-gray-100 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">3. Contornos</h3>
                                <span className="text-xs font-bold text-sura-blue bg-gray-50 px-2 py-1 rounded-md">MÁXIMO (02) CONTORNOS</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {menu.Almuerzo.contornos.map(item => {
                                    const contorno = item.nombre;
                                    const isSelected = formData.contornos.includes(contorno);
                                    return (
                                        <button key={contorno} type="button" onClick={() => handleContornoToggle(contorno)}
                                            className={`text-left px-3 py-2 rounded-xl text-xs font-medium border transition-colors flex items-center justify-between gap-2
                                            ${isSelected ? 'bg-sura-blue text-white border-sura-blue shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-100'}`}>
                                            <span className="truncate">{contorno}</span>
                                            <span className="flex items-center gap-1 shrink-0">
                                                {isSelected && <Check size={14} />}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Menú Cena */}
                {tipoComida === 'Cena' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">2. Comida Principal</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs text-sura-blue mb-1">Categoría</label>
                                <select value={formData.tipo_categoria} onChange={e => setFormData({ ...formData, tipo_categoria: e.target.value, comida_principal: '' })}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue">
                                    <option value="">Seleccione...</option>
                                    {Object.keys(menu.Cena.platos).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-sura-blue mb-1">Plato</label>
                                <select value={formData.comida_principal} onChange={e => setFormData({ ...formData, comida_principal: e.target.value })} disabled={!formData.tipo_categoria}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue disabled:opacity-50">
                                    <option value="">Seleccione un plato...</option>
                                    {formData.tipo_categoria && menu.Cena.platos[formData.tipo_categoria]?.map(plato => <option key={plato.id} value={plato.nombre}>{plato.nombre}</option>)}
                                </select>
                            </div>
                        </div>
                        {/* Mostrar ingredientes de la cena si seleccionó algo */}
                        {formData.comida_principal && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100 flex gap-3">
                                <Info size={18} className="text-gray-400 mt-0.5" />
                                <div>
                                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Ingredientes</p>
                                    <p className="text-sm text-gray-600">
                                        {menu.Cena.platos[formData.tipo_categoria]?.find(p => p.nombre === formData.comida_principal)?.descripcion}
                                    </p>
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* Refrigerio */}
                {tipoComida === REFRIGERIO && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">2. Arma tu Refrigerio</h3>
                        <div className="grid grid-cols-1 md:grid-cols-[10rem_10rem_1fr] gap-4">
                            <div>
                                <label htmlFor="refri-hora" className="block text-xs text-sura-blue mb-1">Hora en que se necesita</label>
                                <input id="refri-hora" type="time" value={refri.hora} onChange={e => setRefri(r => ({ ...r, hora: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue" required />
                            </div>
                            <div>
                                <label htmlFor="refri-personas" className="block text-xs text-sura-blue mb-1">Personas</label>
                                <input id="refri-personas" type="number" inputMode="numeric" min="1" max="500" step="1" placeholder="Ej: 10"
                                    value={refri.personas} onChange={e => setRefri(r => ({ ...r, personas: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue" required />
                            </div>
                            <div>
                                <label htmlFor="refri-motivo" className="block text-xs text-sura-blue mb-1">Motivo del evento</label>
                                <input id="refri-motivo" type="text" maxLength={150} placeholder="Ej: Reunión de cierre de mes"
                                    value={refri.motivo} onChange={e => setRefri(r => ({ ...r, motivo: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue" required />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-xs text-sura-blue">¿Qué te gustaría como refrigerio? Puedes elegir varios</p>
                                {refri.items.length > 0 && <span className="text-xs font-bold text-sura-blue bg-gray-50 px-2 py-1 rounded-md">{refri.items.length} elegido{refri.items.length === 1 ? '' : 's'}</span>}
                            </div>
                            {menu.Refrigerio.refrigerios.length === 0 ? (
                                <p className="text-sm text-gray-500 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    No hay refrigerios disponibles en este momento. Si crees que es un error, avísale a Procura.
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {menu.Refrigerio.refrigerios.map(item => {
                                        const elegido = refri.items.includes(item.nombre);
                                        return (
                                            <button key={item.id} type="button" aria-pressed={elegido}
                                                onClick={() => setRefri(r => ({
                                                    ...r,
                                                    items: elegido ? r.items.filter(n => n !== item.nombre) : [...r.items, item.nombre],
                                                }))}
                                                className={`text-left px-4 py-3 rounded-xl border-2 transition-colors flex items-start justify-between gap-3
                                                    ${elegido ? 'border-sura-blue bg-gray-50' : 'border-gray-100 hover:bg-gray-50/50'}`}>
                                                <span className="min-w-0">
                                                    <span className={`block text-sm font-semibold ${elegido ? 'text-sura-blue' : 'text-gray-700'}`}>{item.nombre}</span>
                                                    {item.descripcion && <span className="block text-xs text-gray-500 mt-0.5">{item.descripcion}</span>}
                                                </span>
                                                <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center ${elegido ? 'border-sura-blue bg-sura-blue' : 'border-gray-300 bg-white'}`}>
                                                    {elegido && <Check size={13} className="text-white" />}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <p className="text-xs text-gray-400 mt-3">Procura decide las cantidades según el número de personas.</p>
                        </div>
                    </motion.div>
                )}

                {/* Bebida y Observaciones */}
                {tipoComida && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Adicionales</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {['Almuerzo', 'Cena'].includes(tipoComida) && (
                                <div>
                                    <label className="block text-xs text-sura-blue mb-1">Bebida</label>
                                    <select value={formData.bebida} onChange={e => setFormData({ ...formData, bebida: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue">
                                        <option value="">Sin bebida</option>
                                        {menu[tipoComida].bebidas.map(b => <option key={b.id} value={b.nombre}>{etiquetaBebida(b)}</option>)}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="block text-xs text-sura-blue mb-1">Restricciones / Observaciones</label>
                                <input type="text" value={formData.observaciones} onChange={e => setFormData({ ...formData, observaciones: e.target.value })}
                                    placeholder={tipoComida === REFRIGERIO ? 'Ej. Llevarlo a la sala de reuniones del piso 2...' : 'Ej. Sin cebolla, alérgico al tomate...'}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue" />
                            </div>
                        </div>
                    </div>
                )}

                {tipoComida && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4 pt-4">
                        <button type="submit" disabled={submitting}
                            className="px-8 py-3 bg-sura-blue hover:bg-sura-blue text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg shadow-gray-100 disabled:opacity-50">
                            {submitting ? <Loader2 size={18} className="animate-spin" /> : (isGroupOrder ? <Plus size={18} /> : <CheckCircle2 size={18} />)}
                            {isGroupOrder ? 'Añadir al Grupo' : 'Enviar Solicitud'}
                        </button>
                    </div>
                )}
            </form>
            )}

            {isGroupOrder && groupOrderCountSet && groupCart.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm mt-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Utensils className="text-sura-blue" size={20} />
                            Resumen del Pedido Grupal
                        </div>
                        <span className="text-sm font-semibold text-sura-blue bg-gray-50 px-3 py-1 rounded-full">
                            Agregados: {groupCart.length} {Number(groupOrderSize) !== 999 && `/ ${groupOrderSize}`}
                        </span>
                    </h3>
                    <div className="space-y-3 mb-6">
                        {groupCart.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <div className="min-w-0">
                                    <p className="font-semibold text-gray-800">{item.empleado_nombre}</p>
                                    {item.is_externo && <p className="text-xs text-gray-500">{EXTERNOS.find(x => x.id === item.externo)?.departamento}</p>}
                                    <p className="text-sm text-gray-600">{item.tipo} - {item.comida_principal}</p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <button
                                        onClick={() => setGroupCart(prev => prev.filter((_, i) => i !== idx))}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <XCircle size={20} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {isGroupOrder && groupOrderCountSet && (
                <div className="pt-4">
                    <button
                        onClick={submitGroupCart}
                        disabled={submitting || groupCart.length === 0}
                        className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg transition-all flex justify-center items-center gap-2 disabled:opacity-50"
                    >
                        {submitting ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle2 size={20} />}
                        Confirmar y Enviar Pedido Grupal
                    </button>
                </div>
            )}
        </motion.div>
    );

    const handleDownloadExcel = async (fecha = reportParams.fecha, tipo = reportParams.tipo) => {
        try {
            const res = await axiosInstance.get(`/api/comidas/reporte/excel?fecha=${fecha}&tipo=${tipo}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Reporte_Comidas_${fecha}_${tipo}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Error al descargar el Excel');
        }
    };

    const renderReportes = () => (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-7xl mx-auto space-y-6">
            <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Consolidado de Pedidos</h2>
                        <p className="text-sm text-sura-blue">Pedidos actualmente en preparación para la fecha seleccionada</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <input type="date" value={reportParams.fecha} onChange={e => setReportParams({...reportParams, fecha: e.target.value})} className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue" />
                        <select value={reportParams.tipo} onChange={e => setReportParams({...reportParams, tipo: e.target.value})} className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue">
                            <option value="Todos">Todos los turnos</option>
                            <option value="Desayuno">Desayuno</option>
                            <option value="Almuerzo">Almuerzo</option>
                            <option value="Cena">Cena</option>
                            <option value={REFRIGERIO}>Refrigerio</option>
                        </select>
                        <button onClick={() => handleDownloadExcel()} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm">
                            <FileSpreadsheet size={16} /> Exportar Detalle
                        </button>
                    </div>
                </div>

                <CierrePedidoDia fecha={reportParams.fecha} turno={reportParams.tipo} onExportar={handleDownloadExcel} />

                {loading ? (
                    <div className="flex justify-center py-12"><Loader2 size={32} className="animate-spin text-gray-400" /></div>
                ) : !reportData || reportData.total === 0 ? (
                    <div className="text-center py-12 text-sura-blue bg-gray-50 rounded-2xl border border-dashed border-gray-200">No hay pedidos en preparación para estos filtros.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Platos */}
                        <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-50">
                            <h3 className="font-bold text-sura-blue mb-4 flex items-center gap-2"><Utensils size={18} /> Platos Principales</h3>
                            <ul className="space-y-3">
                                {reportData.por_plato.map((p, i) => (
                                    <li key={i} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-50 shadow-sm">
                                        <span className="text-sm font-medium text-gray-700">{p.nombre}</span>
                                        <span className="text-sm font-bold text-sura-blue bg-gray-50 px-2 py-0.5 rounded-full">{p.cantidad}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {/* Contornos */}
                        <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-50">
                            <h3 className="font-bold text-sura-blue mb-4 flex items-center gap-2"><Pizza size={18} /> Contornos</h3>
                            <ul className="space-y-3">
                                {reportData.por_contorno.map((p, i) => (
                                    <li key={i} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-50 shadow-sm">
                                        <span className="text-sm font-medium text-gray-700">{p.nombre}</span>
                                        <span className="text-sm font-bold text-sura-blue bg-gray-50 px-2 py-0.5 rounded-full">{p.cantidad}</span>
                                    </li>
                                ))}
                                {reportData.por_contorno.length === 0 && <li className="text-sm text-sura-blue italic">No hay contornos registradas</li>}
                            </ul>
                        </div>
                        {/* Bebidas */}
                        <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-50">
                            <h3 className="font-bold text-sura-blue mb-4 flex items-center gap-2"><Coffee size={18} /> Bebidas</h3>
                            <ul className="space-y-3">
                                {reportData.por_bebida.map((p, i) => (
                                    <li key={i} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-50 shadow-sm">
                                        <span className="text-sm font-medium text-gray-700">{p.nombre}</span>
                                        <span className="text-sm font-bold text-sura-blue bg-gray-50 px-2 py-0.5 rounded-full">{p.cantidad}</span>
                                    </li>
                                ))}
                                {reportData.por_bebida.length === 0 && <li className="text-sm text-sura-blue italic">No hay bebidas registradas</li>}
                            </ul>
                        </div>
                        {/* Refrigerios: unidades de los pedidos ya armados */}
                        {((reportData.por_refrigerio || []).length > 0 || reportData.refrigerios_por_armar > 0) && (
                            <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-50 md:col-span-3">
                                <h3 className="font-bold text-sura-blue mb-4 flex items-center gap-2"><Cookie size={18} /> Refrigerios</h3>
                                {reportData.refrigerios_por_armar > 0 && (
                                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg mb-3">
                                        {reportData.refrigerios_por_armar === 1 ? 'Hay 1 refrigerio' : `Hay ${reportData.refrigerios_por_armar} refrigerios`} por armar en la Bandeja Procura: no suman unidades todavía.
                                    </p>
                                )}
                                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {(reportData.por_refrigerio || []).map((p, i) => (
                                        <li key={i} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-50 shadow-sm">
                                            <span className="text-sm font-medium text-gray-700">{p.nombre}</span>
                                            <span className="text-sm font-bold text-sura-blue bg-gray-50 px-2 py-0.5 rounded-full tabular-nums">{p.cantidad.toLocaleString('es-VE')} und</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </motion.div>
    );

    // ============================================
    // COLUMNAS PARA TABLAS
    // ============================================
    // En la bandeja de Procura se ve además el gasto: monto y centro de costo.
    const conGasto = viewMode === 'bandeja' && esProcura;
    const getColumns = () => [
        { header: 'Solicitante', id: 'solicitante', cell: ({ row }) => (
            <div className="font-medium text-gray-900">{row.original.solicitante_nombre}</div>
        ) },
        { header: 'Fecha de Consumo', accessorKey: 'fecha_consumo', cell: ({ row }) => <div className="text-sm font-medium">{row.original.fecha_consumo}</div> },
        { header: 'Hora Solicitud', accessorKey: 'created_at', cell: ({ row }) => {
            if (!row.original.created_at) return <span className="text-xs text-gray-400">N/A</span>;
            const dt = new Date(row.original.created_at);
            return (
                <div className="whitespace-nowrap leading-tight">
                    <div className="text-sm text-gray-700">{dt.toLocaleDateString('es-VE')}</div>
                    <div className="text-xs text-sura-blue font-medium">{dt.toLocaleTimeString('es-VE', {hour: '2-digit', minute:'2-digit'})}</div>
                </div>
            );
        }},
        { header: 'Turno', accessorKey: 'tipo', cell: ({ row }) => esRefrigerio(row.original)
            ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-full"><Cookie size={12} /> Refrigerio</span>
            : <span className="text-sm font-medium">{row.original.tipo}</span> },
        { header: 'Comida', id: 'comida', cell: ({ row }) => esRefrigerio(row.original) ? (
            <ComidaRefrigerio sol={row.original} />
        ) : <div className="text-sm text-gray-600 max-w-xs">{row.original.comida_principal} {row.original.contorno_1 && ` + ${row.original.contorno_1}`} {row.original.contorno_2 && ` + ${row.original.contorno_2}`}</div> },
        ...(conGasto ? [{ header: 'Monto', id: 'monto', cell: ({ row }) => <MontoCelda sol={row.original} /> }] : []),
        { header: 'Estado', accessorKey: 'estado', cell: ({ row }) => <EstadoBadge estado={row.original.estado} /> },
        { header: 'Acciones', id: 'acciones', cell: ({ row }) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
                <button onClick={() => setSelectedSol(row.original)} className="text-sura-blue hover:text-sura-blue text-sm font-medium px-3 py-1.5 bg-gray-50 rounded-lg hover:bg-gray-50 transition-colors">
                    Ver detalle
                </button>
                {viewMode === 'list' && esCancelable(row.original) && (
                    <button onClick={() => cancelarSolicitud(row.original)} className="text-red-600 text-sm font-medium px-3 py-1.5 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                        Quitar
                    </button>
                )}
                {conGasto && esRefrigerio(row.original) && esCancelable(row.original) && (
                    <button onClick={() => setSolArmar(row.original)} className="text-white text-sm font-medium px-3 py-1.5 bg-sura-blue rounded-lg hover:bg-sura-blue-dark transition-colors">
                        {row.original.refrigerio?.armado ? 'Editar pedido' : 'Armar pedido'}
                    </button>
                )}
            </div>
        )}
    ];

    const pestanas = [
        { id: 'create', label: 'Nueva Solicitud' },
        { id: 'list', label: 'Mis Solicitudes' },
        ...(isSupervisor ? [{ id: 'aprobaciones', label: 'Aprobaciones', badge: pendientesAprobar.length }] : []),
        ...(esProcura ? [
            { id: 'bandeja', label: 'Bandeja Procura' },
            { id: 'reportes', label: 'Reportes de Comida' },
            { id: 'menu', label: 'Menú' },
            { id: 'dashboard', label: 'Dashboard' },
        ] : []),
    ];

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Header, igual que Permisos y Vacaciones */}
            <div className="mb-8 space-y-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Solicitud de Comida</h1>
                    <p className="text-gray-500 mt-1">Servicio de alimentación en el CDN</p>
                </div>
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
                    {pestanas.map(t => (
                        <button key={t.id} type="button" onClick={() => setViewMode(t.id)}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${viewMode === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                            {t.label}
                            {t.badge > 0 && <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{t.badge}</span>}
                        </button>
                    ))}
                </div>
            </div>

            <main>
                {viewMode === 'create' && renderCreate()}

                {viewMode === 'list' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                        <div className="flex justify-end pb-2">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-semibold text-gray-700">Fecha:</label>
                                <input 
                                    type="date" 
                                    value={fechaMisSolicitudes} 
                                    onChange={e => setFechaMisSolicitudes(e.target.value)} 
                                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue"
                                />
                            </div>
                        </div>
                        <TableView columnVisibility={columnVisibility} setColumnVisibility={setColumnVisibility} columns={getColumns()} data={processedMisSolicitudes} loading={loading} emptyMessage="No has realizado solicitudes de comida para esta fecha" />
                    </motion.div>
                )}

                {viewMode === 'aprobaciones' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6">
                            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2"><CheckCircle2 className="text-sura-blue"/> Solicitudes por Aprobar</h2>
                            <p className="text-sm text-sura-blue">Comidas solicitadas por tus supervisados, pendientes de tu aprobación.</p>
                        </div>
                        <TableView columnVisibility={columnVisibility} setColumnVisibility={setColumnVisibility} columns={getColumns()} data={pendientesAprobar} loading={loading} emptyMessage="No tienes solicitudes pendientes por aprobar" />
                    </motion.div>
                )}

                {viewMode === 'bandeja' && esProcura && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2">
                            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
                                {['all', 'en_gestion', 'completada', 'rechazada', 'cancelada'].map(estado => (
                                    <button key={estado} onClick={() => setFilterEstado(estado)}
                                        className={`whitespace-nowrap px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${filterEstado === estado ? 'bg-gray-800 text-white border-gray-800 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                                        {estado === 'all' ? 'Todas' : ESTADO_CONFIG[estado]?.label}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-semibold text-gray-700">Fecha:</label>
                                <input 
                                    type="date" 
                                    value={fechaBandeja} 
                                    onChange={e => setFechaBandeja(e.target.value)} 
                                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue"
                                />
                            </div>
                        </div>
                        <TableView columnVisibility={columnVisibility} setColumnVisibility={setColumnVisibility} columns={getColumns()} data={processedBandeja} loading={loading} emptyMessage="No hay solicitudes para esta fecha" />
                    </motion.div>
                )}

                {viewMode === 'reportes' && esProcura && renderReportes()}

                {viewMode === 'menu' && esProcura && <MenuComidaAdmin onChange={cargarMenu} />}

                {viewMode === 'dashboard' && esProcura && <ComidaDashboard />}
            </main>

            <AnimatePresence>
                {showConfirmGroupModal && selectedGroupToConfirm && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col p-6 text-center space-y-4">
                            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-2">
                                <CheckCircle2 size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900">¿Entregar pedido grupal?</h3>
                            <p className="text-sm text-sura-blue">
                                Estás a punto de marcar como entregadas las <strong>{selectedGroupToConfirm.items.filter(i => ESTADOS_CANCELABLES.includes(i.estado)).length}</strong> solicitudes de este grupo. Esta acción no se puede deshacer.
                            </p>
                            <div className="flex gap-3 pt-4">
                                <button 
                                    onClick={() => { setShowConfirmGroupModal(false); setSelectedGroupToConfirm(null); }} 
                                    className="flex-1 px-5 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                                    disabled={submitting}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    onClick={confirmMarcarGrupoEntregado} 
                                    className="flex-1 px-5 py-3 text-white bg-green-600 hover:bg-green-700 rounded-xl font-medium transition-colors shadow-lg shadow-green-600/20 flex items-center justify-center gap-2"
                                    disabled={submitting}
                                >
                                    {submitting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                                    Sí, entregar
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Confirmación de Cancelar Pedido Grupal */}
            <AnimatePresence>
                {showCancelGroupModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col p-6 text-center space-y-4">
                            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-500 mb-2">
                                <AlertTriangle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900">¿Cancelar Pedido Grupal?</h3>
                            <p className="text-sm text-sura-blue">
                                Tienes personas agregadas a tu pedido grupal actual. Si cambias a "Mi Pedido" se borrará todo tu progreso.
                            </p>
                            <div className="flex gap-3 pt-4">
                                <button 
                                    onClick={() => setShowCancelGroupModal(false)} 
                                    className="flex-1 px-5 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                                >
                                    Volver
                                </button>
                                <button 
                                    onClick={() => {
                                        setShowCancelGroupModal(false);
                                        setIsGroupOrder(false); 
                                        setGroupOrderCountSet(false); 
                                        setGroupCart([]);
                                        setSelectedEmpleadoId('');
                                    }} 
                                    className="flex-1 px-5 py-3 text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-600/20 rounded-xl font-medium transition-colors"
                                >
                                    Sí, Cancelarlo
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Confirmación de Quitar Solicitud */}
            <AnimatePresence>
                {solACancelar && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col p-6 text-center space-y-4">
                            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-500 mb-2">
                                <AlertTriangle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900">¿Quitar solicitud?</h3>
                            <p className="text-sm text-sura-blue">
                                <strong>{solACancelar.solicitante_nombre}</strong> ya no recibirá su <strong>{solACancelar.tipo}</strong> del {solACancelar.fecha_consumo}. La solicitud quedará como cancelada.
                            </p>
                            <div className="flex gap-3 pt-4">
                                <button 
                                    onClick={() => setSolACancelar(null)} 
                                    className="flex-1 px-5 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                                    disabled={submitting}
                                >
                                    Volver
                                </button>
                                <button 
                                    onClick={confirmarCancelacion} 
                                    className="flex-1 px-5 py-3 text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-600/20 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                                    disabled={submitting}
                                >
                                    {submitting && <Loader2 size={18} className="animate-spin" />}
                                    Sí, quitar
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal de Detalle */}
            <AnimatePresence>
                {selectedSol && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {selectedSol.isGroup ? "Pedido Grupal" : `Solicitud #${selectedSol.id}`}
                                    </h3>
                                    <p className="text-sm text-sura-blue">Para el {selectedSol.fecha_consumo} • {selectedSol.tipo}</p>
                                    {selectedSol.created_at && (
                                        <p className="text-xs text-gray-400 mt-1">Cargada el {new Date(selectedSol.created_at).toLocaleString('es-VE')}</p>
                                    )}
                                </div>
                                <button onClick={() => setSelectedSol(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"><XCircle size={24} /></button>
                            </div>
                            
                            <div className="p-6 overflow-y-auto space-y-6">
                                {selectedSol.isGroup ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <h4 className="font-bold text-gray-800 text-lg flex items-center gap-2"><Utensils size={20} className="text-sura-blue"/> Contenido del Pedido Grupal</h4>
                                            {esProcura && (
                                                <div className="text-right">
                                                    <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Monto total</p>
                                                    <MontoCelda sol={selectedSol} className="text-base" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                                            {selectedSol.items.map((item, idx) => (
                                                <div key={item.id || idx} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                                                    <div>
                                                        <p className="font-bold text-gray-800">{item.solicitante_nombre}</p>
                                                        <p className="text-xs text-sura-blue mb-1">
                                                            {item.departamento}
                                                            {esProcura && etiquetaCentroCosto(item) && <span className="text-gray-400"> · CC {etiquetaCentroCosto(item)}</span>}
                                                        </p>
                                                        <p className="text-sm font-medium text-sura-blue">{item.comida_principal} {item.contorno_1 && `+ ${item.contorno_1}`} {item.contorno_2 && `+ ${item.contorno_2}`}</p>
                                                        {item.bebida && <p className="text-xs text-sura-blue mt-1 flex items-center gap-1"><Coffee size={12}/> {item.bebida}</p>}
                                                        {item.observaciones && <p className="text-xs text-sura-blue mt-1 italic">Obs: {item.observaciones.replace(/\[Pedido Grupal.*?\]/g, '').trim()}</p>}
                                                    </div>
                                                    <div className="text-right flex flex-col items-end gap-2">
                                                        <EstadoBadge estado={item.estado} />
                                                        {esProcura && <MontoCelda sol={item} />}
                                                        {esCancelable(item) && (
                                                            <button onClick={() => cancelarSolicitud(item)} className="text-xs font-medium text-red-600 px-2.5 py-1 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                                                                Quitar
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-2 gap-6">
                                            <div>
                                                <p className="text-xs text-sura-blue uppercase tracking-wider font-semibold mb-1">Solicitante</p>
                                                <p className="text-sm font-medium text-gray-900">{selectedSol.solicitante_nombre}</p>
                                                <p className="text-xs text-sura-blue mt-0.5">{selectedSol.departamento}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-sura-blue uppercase tracking-wider font-semibold mb-1">Estado</p>
                                                <EstadoBadge estado={selectedSol.estado} />
                                            </div>
                                        </div>
                                        
                                        {esRefrigerio(selectedSol) ? (
                                        <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-50/50 space-y-4">
                                            <h4 className="font-bold text-gray-900 flex items-center gap-2"><Cookie size={18} className="text-rose-500" /> Refrigerio</h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                <div className="sm:col-span-3">
                                                    <p className="text-xs text-gray-500 mb-1">Motivo</p>
                                                    <p className="text-sm font-semibold text-gray-900">{selectedSol.refrigerio?.motivo}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 mb-1">Personas</p>
                                                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5"><Users size={14} className="text-gray-400" /> {selectedSol.refrigerio?.personas}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 mb-1">Hora</p>
                                                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5"><Clock size={14} className="text-gray-400" /> {hora12(selectedSol.refrigerio?.hora)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 mb-1">Pidió</p>
                                                    <p className="text-sm font-medium text-gray-900">{(selectedSol.refrigerio?.items || []).join(', ')}</p>
                                                </div>
                                            </div>
                                            {esProcura && (
                                                <div className="pt-3 border-t border-gray-100">
                                                    <p className="text-xs text-gray-500 mb-1">Pedido de Procura</p>
                                                    {selectedSol.refrigerio?.armado
                                                        ? <p className="text-sm font-medium text-gray-900">{selectedSol.refrigerio.texto_pedido}</p>
                                                        : <BadgePorArmar />}
                                                </div>
                                            )}
                                        </div>
                                        ) : (
                                        <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-50/50">
                                            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Utensils size={18} className="text-sura-blue" /> Elección de Comida</h4>
                                            <div className="space-y-2">
                                                <div className="flex justify-between border-b border-gray-50/50 pb-2">
                                                    <span className="text-sm text-sura-blue">Plato Principal</span>
                                                    <span className="text-sm font-semibold text-gray-900">{selectedSol.comida_principal}</span>
                                                </div>
                                                {(selectedSol.contorno_1 || selectedSol.contorno_2) && (
                                                    <div className="flex justify-between border-b border-gray-50/50 pb-2 pt-1">
                                                        <span className="text-sm text-sura-blue">Contornos</span>
                                                        <span className="text-sm font-medium text-gray-800 text-right">
                                                            {selectedSol.contorno_1} {selectedSol.contorno_2 && <><br/>{selectedSol.contorno_2}</>}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedSol.bebida && (
                                                    <div className="flex justify-between pt-1">
                                                        <span className="text-sm text-sura-blue flex items-center gap-1"><Coffee size={14}/> Bebida</span>
                                                        <span className="text-sm font-medium text-gray-800">{selectedSol.bebida}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        )}

                                        {esProcura && (
                                            <div className="rounded-2xl border border-gray-100 p-5">
                                                <h4 className="font-bold text-gray-900 mb-3">Gasto</h4>
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Monto</p>
                                                        <MontoCelda sol={selectedSol} className="text-base" />
                                                        {selectedSol.monto_incompleto && !esRefrigerio(selectedSol) && (
                                                            <p className="text-[11px] text-amber-700 mt-1">Algún ítem no tenía precio al pedir</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Centro de costo</p>
                                                        {selectedSol.centro_costo ? (
                                                            <p className="text-sm font-medium text-gray-900">
                                                                {selectedSol.centro_costo}
                                                                {selectedSol.centro_costo_nombre && <span className="block text-xs font-normal text-gray-500">{selectedSol.centro_costo_nombre}</span>}
                                                            </p>
                                                        ) : <p className="text-xs text-gray-300 italic">Sin centro de costo</p>}
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Departamento del gasto</p>
                                                        {selectedSol.departamento_gasto || selectedSol.departamento
                                                            ? <p className="text-sm font-medium text-gray-900">{selectedSol.departamento_gasto || selectedSol.departamento}</p>
                                                            : <p className="text-xs text-gray-300 italic">Sin departamento</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {selectedSol.observaciones && (
                                            <div>
                                                <p className="text-xs text-sura-blue uppercase tracking-wider font-semibold mb-1">Observaciones</p>
                                                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100 italic">{selectedSol.observaciones}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Acciones */}
                            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                                {selectedSol.isGroup && esProcura && selectedSol.items.some(i => ESTADOS_CANCELABLES.includes(i.estado)) && (
                                    <button 
                                        onClick={() => handleMarcarGrupoEntregado(selectedSol)}
                                        disabled={submitting}
                                        className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg shadow-green-600/20 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                                    >
                                        {submitting ? <Loader2 className="animate-spin" size={20}/> : <CheckCircle2 size={20}/>}
                                        Marcar TODO el grupo como Entregado
                                    </button>
                                )}

                                {viewMode === 'list' && esCancelable(selectedSol) && (
                                    <button onClick={() => cancelarSolicitud(selectedSol)} className="px-5 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl text-sm font-medium transition-colors">Quitar solicitud</button>
                                )}
                                {!selectedSol.isGroup && viewMode === 'aprobaciones' && selectedSol.estado === 'pendiente_aprobacion' && (
                                    <>
                                        <button onClick={() => cambiarEstado(selectedSol.id, 'rechazada', window.prompt('Motivo de rechazo:'))} className="px-5 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl text-sm font-medium transition-colors">Rechazar</button>
                                        <button onClick={() => cambiarEstado(selectedSol.id, 'en_gestion')} className="px-5 py-2.5 text-white bg-green-600 hover:bg-green-700 shadow-md shadow-green-600/20 rounded-xl text-sm font-medium transition-colors">Aprobar</button>
                                    </>
                                )}
                                {!selectedSol.isGroup && viewMode === 'bandeja' && esProcura && esCancelable(selectedSol) && (
                                    <>
                                        {selectedSol.estado === 'aprobada' && (
                                            <button onClick={() => cambiarEstado(selectedSol.id, 'en_gestion')} className="px-5 py-2.5 text-white bg-sura-blue hover:bg-sura-blue shadow-md rounded-xl text-sm font-medium transition-colors">Tomar Gestión</button>
                                        )}
                                        {esRefrigerio(selectedSol) && (
                                            <button onClick={() => setSolArmar(selectedSol)} className="px-5 py-2.5 text-white bg-sura-blue hover:bg-sura-blue-dark shadow-md rounded-xl text-sm font-medium transition-colors">
                                                {selectedSol.refrigerio?.armado ? 'Editar pedido' : 'Armar pedido'}
                                            </button>
                                        )}
                                        {selectedSol.estado === 'en_gestion' && (
                                            <button onClick={() => cambiarEstado(selectedSol.id, 'completada')} disabled={porArmar(selectedSol)}
                                                title={porArmar(selectedSol) ? 'Primero arma el pedido del refrigerio' : undefined}
                                                className="px-5 py-2.5 text-white bg-emerald-600 hover:bg-emerald-700 shadow-md rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Marcar Entregada</button>
                                        )}
                                        <button onClick={() => cancelarSolicitud(selectedSol)} className="px-5 py-2.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium transition-colors">Quitar</button>
                                        <button onClick={() => cambiarEstado(selectedSol.id, 'rechazada')} className="px-5 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl text-sm font-medium transition-colors">Rechazar</button>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Armar el pedido de un refrigerio (Procura) */}
            <AnimatePresence>
                {solArmar && (
                    <ArmarRefrigerio
                        key={`armar-${solArmar.id}`}
                        sol={solArmar}
                        onClose={() => setSolArmar(null)}
                        onGuardado={(actualizada) => {
                            setSolArmar(null);
                            setSelectedSol(prev => (prev && prev.id === actualizada.id ? actualizada : prev));
                            fetchData();
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default SolicitudComidaView;
